import { Dispatch, SetStateAction, useEffect } from 'react';

import { JOB_STATUSES } from '@helpers';
import { checkEvent } from '@reducers/utils';
import { OperationSearchItem } from '@interfaces/search';

interface UseListenForClosedOperationsParams {
  isActiveInstance: boolean;
  setJobs: Dispatch<SetStateAction<OperationSearchItem[]>>;
}

// Listens for SignalR `OnClosedProductionOperation` and marks the matching
// row as Finished in-place so the row + Mark As Finished trigger reflect without a refetch.
// On unmount we abort the controller - `checkEvent` tears down the SignalR subscription
// immediately and rejects the pending await, so the while-loop exits without a stale setState.
export const useListenForClosedOperations = ({ isActiveInstance, setJobs }: UseListenForClosedOperationsParams) => {
  useEffect(() => {
    if (!isActiveInstance) return;
    const controller = new AbortController();
    (async () => {
      while (!controller.signal.aborted) {
        try {
          const data = (await checkEvent('OnClosedProductionOperation', { signal: controller.signal })) as {
            productionOperationId: number;
          };
          if (controller.signal.aborted) return;
          setJobs((prev) =>
            prev.map((j) => (j.productionOperationId === data.productionOperationId ? { ...j, status: JOB_STATUSES.FINISHED } : j))
          );
        } catch {
          // Re-arm on timeout/error so the listener keeps running for the lifetime of the screen.
          // On abort the next while-check exits the loop.
        }
      }
    })();
    return () => {
      controller.abort();
    };
  }, [isActiveInstance, setJobs]);
};
